import React, { useState, useEffect } from "react";
import Button from "../ui/Button";
import "./../../styles/SupportDashboard.css";
import "./../../styles/RiskLevelModal.css";
import riskService from "../../services/RiskService";

const riskFlagRequests = new Map();

const getRiskFlagsOnce = (deviceId) => {
  if (riskFlagRequests.has(deviceId)) {
    return riskFlagRequests.get(deviceId);
  }

  const request = riskService
    .getRiskFlags({ deviceId })
    .finally(() => riskFlagRequests.delete(deviceId));

  riskFlagRequests.set(deviceId, request);
  return request;
};

export default function RiskLevelModal({ isOpen, onClose, onSave, nominal }) {
  const getTodayDate = () => new Date().toISOString().split("T")[0];

  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split("T")[0];
  };

  const [riskLevel, setRiskLevel] = useState(null);
  const [category, setCategory] = useState("A");
  const [expiryDate, setExpiryDate] = useState(getDefaultExpiryDate());
  const [noExpiryDate, setNoExpiryDate] = useState(false);
  const [notes, setNotes] = useState("");
  const [existingRiskData, setExistingRiskData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState("");


  const riskCategoryOptions = [
  { label: 'A - Illegal activity', value: 'A' },
  { label: 'B - Illegal images', value: 'B' },
  { label: 'C - Suspect images (Teen)', value: 'C' },
  { label: 'D - Extreme Pornography', value: 'D' },
  { label: 'E - Pornography', value: 'E' },
  { label: 'F - Suspect nudity', value: 'F' },
  { label: 'G - Nudity', value: 'G' },
  { label: 'H - Grooming', value: 'H' },
  { label: 'I - Stranger Danger', value: 'I' }, 
  { label: 'J - Sexting', value: 'J' },
  { label: 'K - SHPO Breach', value: 'K' },
  { label: 'L - Self-Harm', value: 'L' },
  { label: 'M - Depression/Negativity', value: 'M' },
  { label: 'N - Illegal Intent', value: 'N' },
  { label: 'O - Extremism', value: 'O' },
  { label: 'P - Concerning', value: 'P' },
  { label: 'Q - Offender Manager Request', value: 'Q' },
  { label: 'R - Computer Misuse', value: 'R' },
  { label: 'S - Weaponry & Violence', value: 'S' }
];
 

  useEffect(() => {
    if (!isOpen || !nominal?.visorId) return undefined;

    let isCurrent = true;

    const fetchRiskData = async () => {
      setIsLoading(true);
      try {
        const data = await getRiskFlagsOnce(nominal.visorId);
        if (!isCurrent) return;

        const normalizedRiskData = Array.isArray(data) ? data[0] : data;
        setExistingRiskData(normalizedRiskData);

        if (normalizedRiskData && normalizedRiskData.level !== undefined) {
          const levelMap = { 2: "high", 1: "medium", 0: "none" };
          setRiskLevel(levelMap[normalizedRiskData.level] || "none");
          setCategory(normalizedRiskData.category || "A");
          setNotes(normalizedRiskData.description || "");

          if (normalizedRiskData.endDate) {
            const dateOnly = normalizedRiskData.endDate.split("T")[0];
            setExpiryDate(dateOnly);
            setNoExpiryDate(false);
          } else {
            setNoExpiryDate(true);
            setExpiryDate("");
          }
        } else {
          setExistingRiskData(null);
          setRiskLevel(null);
          setCategory("A");
          setNotes("");
          setNoExpiryDate(false);
          setExpiryDate(getDefaultExpiryDate());
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error fetching risk flags:", error);
        setExistingRiskData(null);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    fetchRiskData();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, nominal?.visorId]);

  const handleSubmit = async () => {
    if (riskLevel === null) {
      setValidationError("Please select a risk level (High, Medium, or None)");
      return;
    }

    if (!noExpiryDate && expiryDate && expiryDate < getTodayDate()) {
      setValidationError("Expiry date cannot be earlier than today");
      return;
    }

    setValidationError("");

    const getEndDate = () => {
       if (noExpiryDate) return "2199-12-31T23:59:59Z";
      const date = new Date(expiryDate + "T23:59:59Z");
      return date.toISOString();
    };

    const getstartDate = () => {
      const date = new Date();
      return date.toISOString();
    };

    const payload = {
      id: existingRiskData?.id,
      deviceId: nominal?.visorId || nominal?.id?.toString() || "",
      level: riskLevel === "high" ? 2 : riskLevel === "medium" ? 1 : 0,
      category,
      description: notes,
      startDate: getstartDate(),
      endDate: getEndDate(),
    };

    try {
      setIsSaving(true);
      const isNewRecord = !existingRiskData;

      if (riskLevel === "none") {
        if (!payload.id) {
          throw new Error("Missing risk flag id for delete request");
        }
        await riskService.deleteRiskFlags({ riskFlagId: payload.id });
      } else if (isNewRecord) {
        await riskService.setRiskFlags(payload);
      } else {
        await riskService.updateRiskFlags(payload);
      }

      onSave({
        nominalId: nominal?.id || nominal?.visorId,
        riskLevel,
        category,
        expiryDate: noExpiryDate ? "2199-12-31" : expiryDate,
        noExpiryDate,
        notes,
      });
      onClose();
    } catch (error) {
      console.error("Error setting risk flags:", error);
      setValidationError(`Error: ${error.message || "Failed to update risk flags"}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop risk-modal-overlay" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md risk-modal">
        <div className="sd-modal-header risk-modal-header">
          <div>
            <h2 className="sd-title">Set Device Risk Level</h2>
            <div className="sd-subtitle">
              Set the risk level for this device, along with any relevant details.
            </div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            x
          </button>
        </div>

        <div className="sd-modal-body risk-modal-body">
          <div className="sd-label">Nominal Details</div>
          <div className="risk-device-card">
            <div className="risk-device-info">
              <h3>{nominal?.visorId || nominal?.visorRef || "VIS-0001"}</h3>
              <span>{nominal?.groupName || "Unknown Group"}</span>
            </div>
          </div>

          {validationError && <div className="sd-alert sd-alert-bad">{validationError}</div>}

          <div className="sd-field">
            <div className="sd-label">Risk Level</div>
            <div className="sd-seg risk-buttons" role="tablist" aria-label="Risk level">
              <button
                type="button"
                role="tab"
                aria-selected={riskLevel === "high"}
                className={`sd-seg-btn risk-seg-btn ${riskLevel === "high" ? "is-active is-high" : ""}`}
                onClick={() => setRiskLevel("high")}
              >
                High Risk
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={riskLevel === "medium"}
                className={`sd-seg-btn risk-seg-btn ${riskLevel === "medium" ? "is-active is-medium" : ""}`}
                onClick={() => setRiskLevel("medium")}
              >
                Medium Risk
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={riskLevel === "none"}
                className={`sd-seg-btn risk-seg-btn ${riskLevel === "none" ? "is-active is-none" : ""}`}
                onClick={() => setRiskLevel("none")}
                disabled={!existingRiskData || existingRiskData?.level === 0}
              >
                None
              </button>
            </div>
            <div className="sd-help">Choose the Risk Level and input further details below, to remove a current Risk level select None.</div>
          </div>

          <div className={`risk-details-panel ${riskLevel && riskLevel !== "none" ? "is-visible" : "is-hidden"}`}>
            <div className="risk-details-inner">
              <div className="risk-form-grid">
                <div className="sd-field">
                  <div className="sd-label">Category</div>
                  <select
                    className="sd-input risk-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {riskCategoryOptions.map((option) => {
                      const { value, label } = option;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="sd-field">
                  <div className="sd-label">Expiry Date</div>
                  <input
                    type="date"
                    className="sd-input risk-date-input"
                    value={noExpiryDate ? "" : expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    min={getTodayDate()}
                    disabled={noExpiryDate}
                  />
                  <label className="risk-checkbox-label">
                    <input
                      type="checkbox"
                      checked={noExpiryDate}
                      onChange={(e) => {
                        setNoExpiryDate(e.target.checked);
                        if (!e.target.checked) {
                          setExpiryDate(getDefaultExpiryDate());
                        }
                      }}
                    />
                    <span>No expiry date</span>
                  </label>
                </div>
              </div>

              <div className="sd-field">
                <div className="sd-label">Notes</div>
                <textarea
                  className="sd-textarea risk-textarea"
                  rows="4"
                  placeholder=""
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
                

        <div className="sd-modal-footer risk-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} buttonStyle="btn-main" disabled={isLoading || isSaving}>
            {isSaving ? "Saving..." : "Save Risk Setting"}
          </Button>
        </div>
      </div>
    </div>
  );
}
